import { getTokensInfo, setTokensInfo } from "@/lib/auth/auth-tokens";
import { client } from "./client.gen";

/**
 * API Client Configuration
 *
 * CRITICAL: This MUST run BEFORE createBrowserRouter() in router.tsx,
 * as route loaders will fire immediately upon router creation and
 * require a configured client for authenticated requests.
 */

const configuredApiUrl = import.meta.env.VITE_API_URL?.trim();
const baseUrl = configuredApiUrl || (import.meta.env.DEV ? "http://localhost:3000" : undefined);

if (!baseUrl) {
  throw new Error("VITE_API_URL must be set for non-development environments");
}

// Configure base URL from environment with credentials for cookie-based auth flows
client.setConfig({
  baseUrl,
  credentials: "include",
});

// Initialize authentication from tokens
const tokens = getTokensInfo();
client.setConfig({
  headers: {
    Authorization: tokens?.token ? `Bearer ${tokens.token}` : undefined,
  },
});

// Auth endpoints where we must NOT attempt a token refresh (prevents infinite loops)
const AUTH_PATHS = ["/auth/refresh", "/auth/email/login", "/auth/email/register"];

// HC-52: refresh proactively when the access token has less than this much
// life left at module load. 60s comfortably covers a slow first request after
// a cold reload near the access-token expiry boundary.
const SAFE_REFRESH_MARGIN_MS = 60_000;

// Refresh token mutex — prevents concurrent refresh calls when multiple
// in-flight requests all receive a 401 simultaneously, AND so the cold-reload
// proactive refresh (maybeRefreshOnBoot) shares the same promise as the 401
// interceptor.
let refreshTokenPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  const currentTokens = getTokensInfo();
  if (!currentTokens?.refreshToken) return false;

  try {
    const response = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: {
        Authorization: `Bearer ${currentTokens.refreshToken}`,
      },
    });

    if (!response.ok) {
      // Refresh token is invalid or expired — user must re-authenticate
      setTokensInfo(null);
      return false;
    }

    const data = (await response.json()) as {
      token: string;
      refreshToken: string;
      tokenExpires: number;
    };
    // setTokensInfo updates cookies AND the client Authorization header
    setTokensInfo(data);
    return true;
  } catch {
    // Network error during refresh — keep existing tokens, don't sign user out
    return false;
  }
}

/**
 * Response interceptor: automatically refresh expired access tokens on 401.
 *
 * The backend issues access tokens that expire in 15 minutes. When one expires,
 * the next API call returns 401. This interceptor transparently refreshes the
 * token using the long-lived refresh token (10-year expiry) and retries the
 * original request — keeping the user logged in without any visible disruption.
 *
 * A mutex ensures that concurrent 401 responses share a single refresh call
 * instead of triggering N parallel refresh requests.
 */
// The 401 retry must rebuild the request, but by the time the response
// interceptor runs, a bodied request (POST/PUT/PATCH) has already been
// consumed by fetch — constructing a Request from it throws a TypeError.
// Stash a pre-flight clone of bodied requests so the retry has a fresh body.
const retryableRequestClones = new WeakMap<Request, Request>();

client.interceptors.request.use((request) => {
  if (request.method !== "GET" && request.method !== "HEAD") {
    retryableRequestClones.set(request, request.clone());
  }
  return request;
});

client.interceptors.response.use(async (response, request) => {
  if (response.status !== 401) return response;

  // Don't attempt refresh on auth endpoints to avoid infinite loops
  if (AUTH_PATHS.some((path) => request.url.includes(path))) {
    return response;
  }

  // Mutex: share a single in-flight refresh across concurrent 401 responses
  if (!refreshTokenPromise) {
    refreshTokenPromise = attemptTokenRefresh().finally(() => {
      refreshTokenPromise = null;
    });
  }

  const refreshed = await refreshTokenPromise;
  if (!refreshed) return response; // Return original 401 — caller handles it

  // Retry the original request with the fresh access token
  const freshTokens = getTokensInfo();
  if (!freshTokens?.token) return response;

  const retryBase = retryableRequestClones.get(request) ?? request;
  const newHeaders = new Headers(retryBase.headers);
  newHeaders.set("Authorization", `Bearer ${freshTokens.token}`);
  return fetch(new Request(retryBase, { headers: newHeaders }));
});

/**
 * HC-52 — Cold-reload proactive refresh.
 *
 * On module load, if the stored access token is within `SAFE_REFRESH_MARGIN_MS`
 * of expiry (or already expired), kick off a refresh BEFORE the router mounts
 * its loaders. We share the existing `refreshTokenPromise` mutex so any 401
 * arriving during the same boot cycle reuses this in-flight call instead of
 * spawning a second refresh.
 *
 * Fire-and-forget on purpose: route loaders that hit 401 will await the same
 * mutex via the response interceptor; we do not want to block the entire app
 * shell on a network round-trip.
 */
export async function maybeRefreshOnBoot(): Promise<void> {
  const tokens = getTokensInfo();
  if (!tokens?.refreshToken) return;

  const remainingMs = tokens.tokenExpires - Date.now();
  if (remainingMs > SAFE_REFRESH_MARGIN_MS) return;

  if (!refreshTokenPromise) {
    refreshTokenPromise = attemptTokenRefresh().finally(() => {
      refreshTokenPromise = null;
    });
  }
  await refreshTokenPromise;
}

// Swallow boot-refresh errors — a network blip during cold reload should not
// surface as an unhandled rejection. Real failures get caught downstream by
// the 401 interceptor or by route loaders.
maybeRefreshOnBoot().catch(() => undefined);
